import { Collapsible } from '@base-ui/react/collapsible'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { runTraceCopy } from '@/copy'
import type { RunDetail, RunStep } from '@/types/api'
import { formatNumber } from '@/utils/format'
import { formatDuration } from '@/utils/time'
import { Alert } from '../Alert/Alert'
import { Button } from '../Button/Button'
import { CodeBlock } from '../CodeBlock/CodeBlock'
import { Disclosure } from '../Disclosure/Disclosure'
import { EmptyState } from '../EmptyState/EmptyState'
import { Icon } from '../Icon/Icon'
import { Skeleton } from '../Loading/Loading'
import { Inline } from '../layout/Inline'
import { lifecycleTone } from '../Status/lifecycle'
import { StatusBadge } from '../Status/Status'
import styles from './RunTrace.module.css'

function jsonStr(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

const DASH = '—'

type StepCells = {
  kind: ReactNode
  duration: string
  tokens: string
  finish: string
  alert: boolean
  body: ReactNode
}

function cellsFor(step: RunStep): StepCells {
  if (step.kind === 'llm_call') {
    return {
      kind: runTraceCopy.kind.llm,
      duration: formatDuration(step.duration_ms),
      tokens: runTraceCopy.tokens(
        formatNumber(step.usage.input_tokens),
        formatNumber(step.usage.output_tokens),
      ),
      finish: step.finish_reason,
      alert: step.finish_reason !== 'stop',
      body: (
        <>
          {step.response_content?.trim() && (
            <CodeBlock title={runTraceCopy.response} wrap>
              {step.response_content}
            </CodeBlock>
          )}
          {step.tool_calls?.length ? (
            <CodeBlock title={runTraceCopy.toolCalls}>
              {step.tool_calls
                .map((tc) => `${tc.name}(${jsonStr(tc.args)})`)
                .join('\n')}
            </CodeBlock>
          ) : null}
          <Disclosure
            label={runTraceCopy.prompt}
            count={String(step.request_messages.length)}
          >
            {step.request_messages.map((m, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: immutable prompt snapshot, never reorders
              <CodeBlock key={`${m.role}-${i}`} title={m.role} wrap>
                {m.content || jsonStr(m.tool_result ?? m.tool_calls ?? '')}
              </CodeBlock>
            ))}
          </Disclosure>
        </>
      ),
    }
  }

  if (step.kind === 'tool_call') {
    return {
      kind: (
        <>
          {runTraceCopy.kind.tool}{' '}
          <span className={styles.name}>{step.name}</span>
        </>
      ),
      duration: formatDuration(step.duration_ms),
      tokens: DASH,
      finish: step.success ? runTraceCopy.ok : runTraceCopy.failed,
      alert: !step.success,
      body: (
        <>
          <CodeBlock title={step.name} caption={runTraceCopy.args}>
            {jsonStr(step.args)}
          </CodeBlock>
          {step.error ? (
            <CodeBlock title={step.name} caption={runTraceCopy.error} wrap>
              {step.error}
            </CodeBlock>
          ) : (
            <CodeBlock title={step.name} caption={runTraceCopy.result}>
              {jsonStr(step.result)}
            </CodeBlock>
          )}
        </>
      ),
    }
  }

  return {
    kind: (
      <>
        {runTraceCopy.kind.nudged}{' '}
        <span className={styles.name}>{step.tool}</span>
      </>
    ),
    duration: DASH,
    tokens: DASH,
    finish: DASH,
    alert: false,
    body: (
      <>
        <p className={styles.note}>{runTraceCopy.nudgedAfter(step.attempts)}</p>
        <CodeBlock title={step.tool} caption={runTraceCopy.lastError} wrap>
          {step.last_error}
        </CodeBlock>
      </>
    ),
  }
}

function Step({ step }: { step: RunStep }) {
  const { kind, duration, tokens, finish, alert, body } = cellsFor(step)
  return (
    <Collapsible.Root className={styles.step}>
      <Collapsible.Trigger className={styles.trigger}>
        <Icon name="chevron-right" size="icon-embedded" />
        <span data-num>{step.seq}</span>
        <span className={styles.kind}>{kind}</span>
        <span data-num>{duration}</span>
        <span data-num>{tokens}</span>
        <span className={styles.finish} data-alert={alert || undefined}>
          {finish}
        </span>
      </Collapsible.Trigger>
      <Collapsible.Panel className={styles.panel}>{body}</Collapsible.Panel>
    </Collapsible.Root>
  )
}

export function RunTrace({
  run,
  steps = [],
  error,
  onRetry,
}: {
  run?: Omit<RunDetail, 'steps'>
  steps?: readonly RunStep[]
  error?: ReactNode
  onRetry?: () => void
}) {
  const status = run?.status
  const previousStatus = useRef<string | undefined>(undefined)
  const [announcement, setAnnouncement] = useState('')

  useEffect(() => {
    if (
      previousStatus.current === 'running' &&
      status &&
      status !== 'running'
    ) {
      setAnnouncement(runTraceCopy.finished(status))
    }
    previousStatus.current = status
  }, [status])

  if (error) {
    return (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {error}
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              {runTraceCopy.retry}
            </Button>
          )}
        </Inline>
      </Alert>
    )
  }

  if (!run) {
    return (
      <div aria-busy="true">
        <div className={styles.header}>
          <Skeleton width="40%" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className={styles.skeletonRow}>
            <Skeleton />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div>
      <div className={styles.header}>
        <span className={styles.id}>{run.id}</span>
        <StatusBadge tone={lifecycleTone(run.status)}>{run.status}</StatusBadge>
        <span className={styles.counts}>
          {runTraceCopy.counts(
            run.llm_calls,
            run.tool_calls,
            formatDuration(run.duration_ms),
          )}
        </span>
      </div>
      <span role="status" className={styles.visuallyHidden}>
        {announcement}
      </span>
      {steps.length === 0 ? (
        <EmptyState variant="inline" description={runTraceCopy.noSteps} />
      ) : (
        <div className={styles.scroll}>
          <div
            className={styles.steps}
            aria-busy={run.status === 'running' || undefined}
          >
            {steps.map((step) => (
              <Step key={step.seq} step={step} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
